import React from 'react';
import classNames from 'classnames';
import { Box, Heading } from '@wix/design-system';
import {
  type SelectButtonProps,
  NonBlockerWrapper,
  ProductDescription,
  useAnonymous,
  useCurrentModule,
  Base44VoucherCard,
  shouldShowBase44Voucher,
  useAiRecommendationEnabled,
  useRecommendationRevealEnabled,
} from '@wix/package-picker-shared-eclipse';
import { useMoneyBackCtaHover, useMoneyBackLabelViewed } from '../../../hooks';
import { ProductCardList } from '@wix/premium-purchase-platform/@components';
import { FeatureList } from './FeatureList';
import {
  MoneyBackHoverLabel,
  moneyBackHoverCtaClassName,
} from '../../MoneyBackHoverLabel';
import { EclipsePrice } from '../../EclipsePrice/EclipsePrice';
import {
  AiPlanRecommendationCard,
  UpgradeRecommendationCard,
} from '../../AiPlanRecommendationCard';
import s from './ProductCard.scss';

interface CardContentProps {
  name?: string;
  isRecommended?: boolean;
  isUpgradeRecommended?: boolean;
  SelectButton: React.FC<SelectButtonProps>;
  productId: string;
}

export const CardContent = ({
  name,
  isRecommended,
  isUpgradeRecommended = false,
  SelectButton,
  productId,
}: CardContentProps) => {
  const { isAnonymous } = useAnonymous();
  const module = useCurrentModule();
  const { experiments } = module.internals.experiments.hooks.useExperiments();
  const { isEnabled: isAiRecommendationEnabled, isBannerVariant } =
    useAiRecommendationEnabled();
  const { isEnabled: isRevealEnabled } = useRecommendationRevealEnabled();
  const { data: product } =
    module.SDK.eclipseOfferingApiClient.hooks.useFetchProduct({ productId });

  const { isEnabled: showMoneyBackHover } = useMoneyBackCtaHover();
  const moneyBackLabelViewedProps = useMoneyBackLabelViewed(showMoneyBackHover);

  const showBase44Voucher = shouldShowBase44Voucher({
    productFamilyId: product?.productFamilyId,
    isExperimentEnabled: experiments.isEnabled('eclipseBase44Voucher'),
  });

  return (
    <>
      <ProductCardList.Default.Card.Header
        height="50px"
        width="100%"
        direction="vertical"
        marginBottom={showMoneyBackHover ? '8px' : '16px'}
        align="left"
      >
        <Heading size="medium" className={s.planTitle}>
          {name}
        </Heading>
        <NonBlockerWrapper componentName="ProductDescription">
          <ProductDescription productId={productId} />
        </NonBlockerWrapper>
      </ProductCardList.Default.Card.Header>
      <ProductCardList.Default.Card.Price
        align="left"
        direction="vertical"
        gap="3px"
      >
        <Box marginBottom="SP3" align="left">
          <EclipsePrice productId={productId} />
        </Box>
      </ProductCardList.Default.Card.Price>
      {!isAnonymous && (
        <Box
          marginBottom={showMoneyBackHover ? 'SP1' : 'SP3'}
          width="100%"
          direction="vertical"
          gap="SP1"
          className={classNames({
            [s.ctaReveal]: isRevealEnabled,
            [moneyBackHoverCtaClassName]: showMoneyBackHover,
          })}
        >
          <SelectButton
            variant={isRecommended ? 'primary' : 'secondary'}
            fullWidth={true}
            {...moneyBackLabelViewedProps}
          />
          {showMoneyBackHover && <MoneyBackHoverLabel />}
        </Box>
      )}
      {isAiRecommendationEnabled && isRecommended && !isBannerVariant && (
        <AiPlanRecommendationCard />
      )}
      {isUpgradeRecommended && <UpgradeRecommendationCard />}
      <FeatureList productId={productId} />

      {showBase44Voucher && <Base44VoucherCard productId={productId} />}
    </>
  );
};
